import { useState, useMemo, useRef } from "react";
import PageSEO from "@/components/PageSEO";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Calculator, TrendingUp, IndianRupee, Clock, ArrowRight, ChevronDown } from "lucide-react";

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41", 600: "#6e5c4e",
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  e500: "#A49E8D", e400: "#8c8678", e300: "#c0b9ac", e100: "#f0ede8",
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

function Reveal({ children, className, delay = 0, y = 25 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const CHARGER_TYPES = [
  { label: "AC 7.4 kW — Home / Retail",      capex: 45000,   pricePerSession: 40,  sessionsPerDay: 8 },
  { label: "AC 22 kW — Workplace / Mall",     capex: 120000,  pricePerSession: 80,  sessionsPerDay: 10 },
  { label: "DC 30 kW — Fleet / Commercial",   capex: 350000,  pricePerSession: 120, sessionsPerDay: 6 },
  { label: "DC 60 kW — Highway / Commercial", capex: 650000,  pricePerSession: 200, sessionsPerDay: 8 },
  { label: "DC 120 kW — HPC / Superhub",      capex: 1200000, pricePerSession: 350, sessionsPerDay: 10 },
];

function fmt(n) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

function Slider({ label, value, min, max, step = 1, onChange, format = v => v }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium" style={{ color: F[800] }}>{label}</label>
        <span
          className="text-sm font-bold px-2 py-0.5 rounded-md"
          style={{ backgroundColor: F[50], color: F[500], fontFamily: "JetBrains Mono, monospace" }}
        >
          {format(value)}
        </span>
      </div>
      <div className="relative">
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${F[500]} ${pct}%, ${F[200]} ${pct}%)`,
            accentColor: F[500],
          }}
        />
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color = F[500] }) {
  return (
    <div
      className="rounded-xl border p-5"
      style={{ backgroundColor: "#fff", borderColor: F[200] }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color }} />
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: F.m400 }}>{label}</span>
      </div>
      <p
        className="text-2xl font-bold mb-0.5"
        style={{ color: F[800], fontFamily: "JetBrains Mono, monospace" }}
      >
        {value}
      </p>
      {sub && <p className="text-xs" style={{ color: F.m400 }}>{sub}</p>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div
      className="rounded-lg border px-3 py-2 text-sm shadow-md"
      style={{ backgroundColor: "#fff", borderColor: F[200] }}
    >
      <p style={{ color: F.m400 }}>Year {label}</p>
      <p style={{ color: val >= 0 ? F[500] : "#ef4444", fontFamily: "JetBrains Mono, monospace", fontWeight: 700 }}>
        {fmt(val)}
      </p>
    </div>
  );
};

export default function ROICalculator() {
  const navigate = useNavigate();

  const [chargerTypeIdx, setChargerTypeIdx] = useState(2);
  const [numChargers, setNumChargers] = useState(4);
  const [sessionsPerDay, setSessionsPerDay] = useState(CHARGER_TYPES[2].sessionsPerDay);
  const [pricePerSession, setPricePerSession] = useState(CHARGER_TYPES[2].pricePerSession);
  const [civilCapex, setCivilCapex] = useState(500000);

  const ct = CHARGER_TYPES[chargerTypeIdx];

  const metrics = useMemo(() => {
    const chargerCapex   = numChargers * ct.capex;
    const totalCapex     = chargerCapex + civilCapex;
    const revenuePerDay  = numChargers * sessionsPerDay * pricePerSession;
    const monthlyRevenue = revenuePerDay * 30;
    const annualRevenue  = monthlyRevenue * 12;
    const paybackYears   = annualRevenue > 0 ? totalCapex / annualRevenue : Infinity;
    const roi10yr        = annualRevenue > 0 ? ((10 * annualRevenue - totalCapex) / totalCapex) * 100 : 0;

    const chartData = Array.from({ length: 11 }, (_, yr) => ({
      year: yr,
      profit: yr * annualRevenue - totalCapex,
    }));

    return { chargerCapex, totalCapex, monthlyRevenue, annualRevenue, paybackYears, roi10yr, chartData };
  }, [ct, numChargers, sessionsPerDay, pricePerSession, civilCapex]);

  function handleChargerTypeChange(e) {
    const idx = Number(e.target.value);
    setChargerTypeIdx(idx);
    setSessionsPerDay(CHARGER_TYPES[idx].sessionsPerDay);
    setPricePerSession(CHARGER_TYPES[idx].pricePerSession);
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: F.c100, fontFamily: "Manrope, sans-serif" }}>
      <PageSEO
        title="EV Charging Station ROI Calculator India — Payback Period & Revenue"
        description="Free ROI calculator for EV charging stations in India. Enter charger count, tariff and utilisation to instantly see monthly revenue, payback period and 10-year cash flow."
        path="/roi-calculator"
      />
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md"
        style={{ backgroundColor: F[800] + "f2", borderBottom: `1px solid ${F[700]}` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <button
            onClick={() => navigate("/")}
            style={{ color: F[200], fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: "1rem" }}
          >
            EV Hub Studio
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/planner")}
              className="text-sm hidden sm:block"
              style={{ color: F[300] }}
            >
              Planner
            </button>
            <button
              onClick={() => navigate("/try")}
              className="text-sm px-4 py-1.5 rounded-lg font-semibold"
              style={{ backgroundColor: F[500], color: "#fff" }}
            >
              Try Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 text-center">
        <Reveal>
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4"
            style={{ backgroundColor: F[500] + "20", color: F[500] }}
          >
            ROI Calculator
          </span>
          <h1
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
          >
            Will your EV charging station be profitable?
          </h1>
          <p className="text-base leading-relaxed" style={{ color: F.m400 }}>
            Adjust the sliders to model your site. Revenue, payback period, and 10-year profit
            update instantly — no sign-up required.
          </p>
        </Reveal>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-6">

          {/* Inputs */}
          <Reveal>
            <div
              className="rounded-2xl border p-6 space-y-6"
              style={{ backgroundColor: "#fff", borderColor: F[200] }}
            >
              <h2
                className="font-bold text-lg"
                style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
              >
                Site inputs
              </h2>

              {/* Charger type */}
              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: F[800] }}>Charger type</label>
                <div className="relative">
                  <select
                    value={chargerTypeIdx}
                    onChange={handleChargerTypeChange}
                    className="w-full appearance-none rounded-lg border px-3 py-2.5 pr-8 text-sm"
                    style={{ borderColor: F[200], color: F[800], backgroundColor: "#fff" }}
                  >
                    {CHARGER_TYPES.map((t, i) => (
                      <option key={i} value={i}>{t.label}</option>
                    ))}
                  </select>
                  <ChevronDown
                    size={15}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ color: F.m400 }}
                  />
                </div>
                <p className="text-xs" style={{ color: F.m400 }}>
                  Capex per unit: <span style={{ fontFamily: "JetBrains Mono, monospace", color: F[500] }}>{fmt(ct.capex)}</span>
                </p>
              </div>

              <Slider
                label="Number of chargers"
                value={numChargers} min={1} max={20}
                onChange={setNumChargers}
                format={v => `${v} charger${v > 1 ? "s" : ""}`}
              />
              <Slider
                label="Sessions per charger per day"
                value={sessionsPerDay} min={1} max={25}
                onChange={setSessionsPerDay}
                format={v => `${v} sessions`}
              />
              <Slider
                label="Revenue per session"
                value={pricePerSession} min={10} max={500} step={5}
                onChange={setPricePerSession}
                format={v => `₹${v}`}
              />
              <Slider
                label="Civil & electrical CAPEX"
                value={civilCapex} min={0} max={5000000} step={50000}
                onChange={setCivilCapex}
                format={v => fmt(v)}
              />

              <div
                className="rounded-lg p-3 text-xs"
                style={{ backgroundColor: F[50], color: F.m500, lineHeight: 1.6 }}
              >
                <strong style={{ color: F[800] }}>Assumptions:</strong> Operating costs (electricity, maintenance)
                estimated at 30% of revenue. Figures are indicative — use the full planner for a detailed model.
              </div>
            </div>
          </Reveal>

          {/* Results */}
          <div className="space-y-4">
            <Reveal delay={0.05}>
              <div className="grid grid-cols-2 gap-3">
                <MetricCard
                  icon={IndianRupee}
                  label="Monthly Revenue"
                  value={fmt(metrics.monthlyRevenue)}
                  sub="gross, before opex"
                />
                <MetricCard
                  icon={TrendingUp}
                  label="Annual Revenue"
                  value={fmt(metrics.annualRevenue)}
                  sub="gross, before opex"
                />
                <MetricCard
                  icon={Clock}
                  label="Payback Period"
                  value={metrics.paybackYears === Infinity ? "—" : `${metrics.paybackYears.toFixed(1)} yrs`}
                  sub="on total CAPEX"
                  color={metrics.paybackYears < 5 ? F[500] : metrics.paybackYears < 8 ? F.e500 : "#ef4444"}
                />
                <MetricCard
                  icon={Calculator}
                  label="10-Year ROI"
                  value={`${Math.round(metrics.roi10yr)}%`}
                  sub={`Total CAPEX: ${fmt(metrics.totalCapex)}`}
                  color={metrics.roi10yr >= 100 ? F[500] : F.e500}
                />
              </div>
            </Reveal>

            {/* Chart */}
            <Reveal delay={0.1}>
              <div
                className="rounded-2xl border p-5"
                style={{ backgroundColor: "#fff", borderColor: F[200] }}
              >
                <p
                  className="font-bold mb-4 text-sm"
                  style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
                >
                  Cumulative profit over 10 years
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={metrics.chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={F[500]} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={F[500]} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="year"
                      tick={{ fontSize: 11, fill: F.m400 }}
                      axisLine={false} tickLine={false}
                      tickFormatter={v => `Yr ${v}`}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: F.m400 }}
                      axisLine={false} tickLine={false}
                      tickFormatter={v => fmt(v)}
                      width={55}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke={F[200]} strokeDasharray="4 4" />
                    <Area
                      type="monotone" dataKey="profit"
                      stroke={F[500]} strokeWidth={2}
                      fill="url(#profitGrad)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Reveal>
          </div>
        </div>

        {/* CTA */}
        <Reveal delay={0.1}>
          <div
            className="mt-8 rounded-2xl p-8 text-center"
            style={{ backgroundColor: F[900] }}
          >
            <h2
              className="text-xl font-bold mb-2"
              style={{ color: F[200], fontFamily: "Chivo, sans-serif" }}
            >
              Want a detailed breakdown?
            </h2>
            <p className="text-sm mb-5" style={{ color: F[300] }}>
              The full planner includes itemised CAPEX, state-specific subsidy matching,
              compliance checklist, site layout, and an investor-ready PDF report.
            </p>
            <button
              onClick={() => navigate("/try")}
              className="flex items-center gap-2 mx-auto px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
              style={{ backgroundColor: F[500], color: "#fff", fontFamily: "Chivo, sans-serif" }}
            >
              Open Full Planner <ArrowRight size={16} />
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
